/**
 * Download utilities for photos
 *
 * Two-level download system:
 * - Level 0 (client): JSZip in browser, up to 1GB (desktop only)
 * - Level 1 (manifest): Download scripts for Mac/Windows, >1GB
 *
 * Mobile devices: download disabled (use desktop)
 */

import JSZip from "jszip";
import type { Photo } from "@/types";
import { logError } from "@/utils/logger";

// Download configuration
const DOWNLOAD_CONFIG = {
  TIMEOUT_MS: 120000, // 2 minutes per file
  MAX_RETRIES: 3,
  RETRY_DELAY_MS: 1000,
} as const;

export type DownloadFormat = "jpeg" | "raw" | "both";

// Thresholds for download behavior
export const DOWNLOAD_THRESHOLDS = {
  DIRECT: 1, // 1 photo: direct download
  CLIENT_ZIP_MAX: 100, // 2-100: client-side ZIP
  CONFIRM_THRESHOLD: 50, // >50: show confirmation dialog
} as const;

// Size-based thresholds for download routing (desktop only)
export const SIZE_THRESHOLDS = {
  CLIENT_ZIP_MAX_MB: 1024, // <=1GB: client-side JSZip (desktop)
  // >1GB: Manifest + download scripts (Level 1)
} as const;

export type DownloadLevel = "client" | "manifest";

/**
 * Determine download level based on estimated size
 */
export function getDownloadLevel(estimatedSizeMB: number): DownloadLevel {
  if (estimatedSizeMB <= SIZE_THRESHOLDS.CLIENT_ZIP_MAX_MB) {
    return "client";
  }
  return "manifest";
}

/**
 * Format timestamp as readable filename: IMG_20231201_143045
 */
function formatTimestampForFilename(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  return `IMG_${year}${month}${day}_${hours}${minutes}${seconds}`;
}

/**
 * Generate filename from photo timestamp (human-readable)
 */
function getFilename(photo: Photo, type: "jpeg" | "raw"): string {
  const ext = type === "jpeg" ? "jpg" : "dng";

  // Always prefer timestamp-based filename for readability
  const timestamp = photo.capturedAt?.toDate?.();
  if (timestamp) {
    return `${formatTimestampForFilename(timestamp)}.${ext}`;
  }

  // Fallback: use photoId
  return `photo_${photo.photoId}.${ext}`;
}

/**
 * Fetch blob from URL with timeout and retry logic
 * R2 URLs are public HTTPS - fetch directly
 */
async function fetchBlobFromUrl(
  url: string,
  retries: number = DOWNLOAD_CONFIG.MAX_RETRIES
): Promise<Blob> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), DOWNLOAD_CONFIG.TIMEOUT_MS);

  try {
    // Cache buster to bypass CDN cached responses without CORS headers
    const separator = url.includes("?") ? "&" : "?";
    const fetchUrl = `${url}${separator}dl=${Date.now()}`;
    const response = await fetch(fetchUrl, { signal: controller.signal });
    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`);
    }

    return await response.blob();
  } catch (error) {
    clearTimeout(timeoutId);

    if (retries > 0 && (error as Error).name !== "AbortError") {
      await new Promise((resolve) => setTimeout(resolve, DOWNLOAD_CONFIG.RETRY_DELAY_MS));
      return fetchBlobFromUrl(url, retries - 1);
    }

    throw error;
  }
}

/**
 * Download a single file from URL
 */
async function downloadFile(url: string, filename: string): Promise<void> {
  const blob = await fetchBlobFromUrl(url);
  const blobUrl = window.URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  window.URL.revokeObjectURL(blobUrl);
}

/**
 * Download a single photo
 * - Single format: direct download (no ZIP)
 * - Both formats: creates a small ZIP to avoid browser blocking multiple downloads
 */
export async function downloadSinglePhoto(
  photo: Photo,
  format: DownloadFormat,
  onProgress?: (message: string) => void
): Promise<{ success: boolean; error?: string }> {
  try {
    const hasRaw = Boolean(photo.rawUrl);

    // Validate format availability
    if (format === "raw" && !hasRaw) {
      return { success: false, error: "RAW file not available for this photo" };
    }

    // For "both" format, create a ZIP to avoid browser blocking multiple downloads
    if (format === "both" && hasRaw) {
      onProgress?.("Preparing download...");

      const zip = new JSZip();
      const timestamp = photo.capturedAt?.toDate?.();
      const baseName = timestamp
        ? formatTimestampForFilename(timestamp)
        : `photo_${photo.photoId}`;

      // Download and add JPEG
      onProgress?.("Downloading JPEG...");
      const jpegBlob = await fetchBlobFromUrl(photo.urls.full);
      zip.file(`${baseName}.jpg`, jpegBlob);

      // Download and add RAW
      onProgress?.("Downloading RAW...");
      const rawBlob = await fetchBlobFromUrl(photo.rawUrl!);
      zip.file(`${baseName}.dng`, rawBlob);

      // Generate and download ZIP
      onProgress?.("Creating ZIP...");
      const zipBlob = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 },
      });

      const blobUrl = window.URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${baseName}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      return { success: true };
    }

    // Single format: direct download
    if (format === "jpeg" || (format === "both" && !hasRaw)) {
      onProgress?.("Downloading JPEG...");
      const filename = getFilename(photo, "jpeg");
      await downloadFile(photo.urls.full, filename);
    }

    if (format === "raw" && hasRaw) {
      onProgress?.("Downloading RAW...");
      const filename = getFilename(photo, "raw");
      await downloadFile(photo.rawUrl!, filename);
    }

    return { success: true };
  } catch (error) {
    logError("Download single photo error", error as Error, {
      photoId: photo.photoId,
      format,
    });
    return { success: false, error: (error as Error).message };
  }
}

/**
 * Create a ZIP file with multiple photos (client-side)
 * Used for 2-10 photos
 */
export async function downloadPhotosAsZip(
  photos: Photo[],
  format: DownloadFormat,
  viewName: string,
  onProgress?: (loaded: number, total: number, message: string) => void
): Promise<{ success: boolean; error?: string }> {
  try {
    const zip = new JSZip();

    // Create root folder with view name
    const safeName = viewName.replace(/[^a-zA-Z0-9-_\s]/g, "_").slice(0, 50);
    const rootFolder = zip.folder(safeName);
    if (!rootFolder) {
      throw new Error("Failed to create ZIP folder");
    }

    // Create subfolders based on format
    const jpegFolder = format !== "raw" ? rootFolder.folder("jpeg") : null;
    const rawFolder = format !== "jpeg" ? rootFolder.folder("raw") : null;

    // Calculate total files to download
    let totalFiles = 0;
    if (format === "jpeg" || format === "both") {
      totalFiles += photos.length;
    }
    if (format === "raw" || format === "both") {
      totalFiles += photos.filter((p) => p.rawUrl).length;
    }

    let loadedFiles = 0;
    let successfulFiles = 0;
    const errors: string[] = [];

    // Download and add files to ZIP
    for (const photo of photos) {
      // Add JPEG
      if (jpegFolder) {
        const filename = getFilename(photo, "jpeg");
        onProgress?.(loadedFiles, totalFiles, `Downloading ${filename}...`);

        try {
          const blob = await fetchBlobFromUrl(photo.urls.full);
          jpegFolder.file(filename, blob);
          successfulFiles++;
        } catch (e) {
          logError("Failed to download JPEG for ZIP", e as Error, { photoId: photo.photoId });
          errors.push(`${filename}: ${(e as Error).message}`);
        }
        loadedFiles++;
      }

      // Add RAW if available and requested
      if (rawFolder && photo.rawUrl) {
        const filename = getFilename(photo, "raw");
        onProgress?.(loadedFiles, totalFiles, `Downloading ${filename}...`);

        try {
          const blob = await fetchBlobFromUrl(photo.rawUrl);
          rawFolder.file(filename, blob);
          successfulFiles++;
        } catch (e) {
          logError("Failed to download RAW for ZIP", e as Error, { photoId: photo.photoId });
          errors.push(`${filename}: ${(e as Error).message}`);
        }
        loadedFiles++;
      }
    }

    // If no files were successfully downloaded, return error
    if (successfulFiles === 0) {
      return { success: false, error: `Failed to download files: ${errors.join(", ")}` };
    }

    onProgress?.(totalFiles, totalFiles, "Creating ZIP file...");

    // Generate ZIP (STORE mode - no compression, JPEG/RAW are already compressed)
    const zipBlob = await zip.generateAsync({
      type: "blob",
      compression: "STORE",
    });

    // Generate ZIP filename
    const dates = photos
      .map((p) => p.capturedAt?.toDate?.())
      .filter(Boolean)
      .sort((a, b) => a!.getTime() - b!.getTime());

    const formatDate = (d: Date) =>
      d.toISOString().slice(0, 10).replace(/-/g, "");

    const dateStart = dates.length > 0 ? formatDate(dates[0]!) : "unknown";
    const dateEnd =
      dates.length > 1 ? formatDate(dates[dates.length - 1]!) : dateStart;

    // Use safeName already defined above for root folder
    const zipFilename = `${safeName.slice(0, 30)}_${dateStart}_${dateEnd}.zip`;

    // Trigger download
    const blobUrl = window.URL.createObjectURL(zipBlob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = zipFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);

    return { success: true };
  } catch (error) {
    logError("Download ZIP error", error as Error, {
      photoCount: photos.length,
      format,
    });
    return { success: false, error: (error as Error).message };
  }
}

/**
 * Check if any photos in selection have RAW files
 */
export function hasRawFiles(photos: Photo[]): boolean {
  return photos.some((p) => Boolean(p.rawUrl));
}

const BYTES_PER_MB = 1024 * 1024;

/**
 * Estimate download size. Prefers exact per-photo sizes recorded by the
 * Android app at upload time (fileSizeBytes / fileSizeDngBytes); falls back
 * to legacy metadata.sizeMB, then to the probed averages for older photos.
 */
export function estimateDownloadSize(
  photos: Photo[],
  format: DownloadFormat
): { sizeMB: number; fileCount: number } {
  let sizeMB = 0;
  let fileCount = 0;

  for (const photo of photos) {
    if (format === "jpeg" || format === "both") {
      sizeMB += photo.fileSizeBytes
        ? photo.fileSizeBytes / BYTES_PER_MB
        : photo.metadata?.sizeMB || _cachedAvgJpegSizeMB;
      fileCount++;
    }
    if ((format === "raw" || format === "both") && photo.rawUrl) {
      sizeMB += photo.fileSizeDngBytes
        ? photo.fileSizeDngBytes / BYTES_PER_MB
        : _cachedAvgRawSizeMB;
      fileCount++;
    }
  }

  return { sizeMB: Math.round(sizeMB), fileCount };
}

// Cached average sizes from HEAD probe (defaults until probed)
let _cachedAvgJpegSizeMB = 3;
let _cachedAvgRawSizeMB = 35;
let _probedKey = "";

/**
 * Sample up to 3 items spread across a list (first, middle, last).
 */
function sampleSpread<T>(items: T[]): T[] {
  if (items.length === 0) return [];
  const indices = [...new Set([0, Math.floor(items.length / 2), items.length - 1])];
  return indices.map((i) => items[i]);
}

async function headContentLength(url: string): Promise<number | null> {
  const separator = url.includes("?") ? "&" : "?";
  const resp = await fetch(`${url}${separator}dl=${Date.now()}`, { method: "HEAD" });
  const len = resp.headers.get("content-length");
  return len ? Number(len) : null;
}

/**
 * Probe a sample of photos via HEAD requests to refine the fallback averages.
 * Only photos WITHOUT exact sizes from Firestore need probing — recent photos
 * carry fileSizeBytes / fileSizeDngBytes and are counted exactly, so this is
 * skipped entirely when the whole selection has exact data.
 */
export async function probePhotoSizes(photos: Photo[]): Promise<void> {
  if (photos.length === 0) return;

  const jpegMissing = photos.filter((p) => !p.fileSizeBytes && !p.metadata?.sizeMB);
  const rawMissing = photos.filter((p) => p.rawUrl && !p.fileSizeDngBytes);
  if (jpegMissing.length === 0 && rawMissing.length === 0) return;

  // Build a key from photo IDs to detect selection changes
  const key = photos.map((p) => p.photoId).join(",");
  if (key === _probedKey) return;
  _probedKey = key;

  try {
    const jpegSizes: number[] = [];
    const rawSizes: number[] = [];

    for (const photo of sampleSpread(jpegMissing)) {
      const len = await headContentLength(photo.urls.full);
      if (len) jpegSizes.push(len);
    }
    for (const photo of sampleSpread(rawMissing)) {
      const len = await headContentLength(photo.rawUrl!);
      if (len) rawSizes.push(len);
    }

    if (jpegSizes.length > 0) {
      const avgBytes = jpegSizes.reduce((a, b) => a + b, 0) / jpegSizes.length;
      _cachedAvgJpegSizeMB = Math.round((avgBytes / BYTES_PER_MB) * 10) / 10;
    }
    if (rawSizes.length > 0) {
      const avgBytes = rawSizes.reduce((a, b) => a + b, 0) / rawSizes.length;
      _cachedAvgRawSizeMB = Math.round((avgBytes / BYTES_PER_MB) * 10) / 10;
    }
  } catch {
    // Keep defaults on failure
  }
}

/**
 * Check if device is mobile
 */
export function isMobileDevice(): boolean {
  return window.matchMedia("(max-width: 768px)").matches;
}
